/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { branch } from 'recompose';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';

import connectToRouter from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Link } from '@coursera/cds-core';
import { ErrorIcon } from '@coursera/cds-icons';
import { SvgCircleWarning } from '@coursera/coursera-ui/svg';

import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { errorMapping, paymentMethods } from 'bundles/payments/common/constants';
import {
  getErrorMessageForHumanReadableCode,
  getErrorMessageForPaymentProcessorCode,
} from 'bundles/payments/components/errorMessages';
import { MAX_CART_VALID_MILLI_SECS } from 'bundles/payments/components/finaid-2021/constants/limits';
import type { WithCourseProps } from 'bundles/payments/components/finaid-2021/enhancers/withCourse';
import withCourse from 'bundles/payments/components/finaid-2021/enhancers/withCourse';
import type { ServerErrorDetails } from 'bundles/payments/lib/paymentErrorUtils';
import savedCartUtils from 'bundles/payments/lib/savedCartUtils';
import { isFinancialAidCeilingVariantCart } from 'bundles/payments/utils/financialAidCeilingUtils';

import _t from 'i18n!nls/payments';

import 'css!./__styles__/PaymentErrors';

type PaymentErrorsProps = {
  submissionErrorCode: string;
  submissionErrorCodeMessageProps?: { [key: string]: any };
  submissionErrorServerDetails?: ServerErrorDetails;
  cart?: CartsV2;
  showCdsDesign?: boolean;
};

type WithCourseIdProps = {
  courseId: string;
};

type PropsFromRouter = {
  isFinancialAid: string;
};

type Props = PaymentErrorsProps & PropsFromRouter & WithCourseProps;

const PaymentErrors = (props: Props) => {
  const {
    submissionErrorCode,
    submissionErrorCodeMessageProps,
    submissionErrorServerDetails,
    cart,
    isFinancialAid,
    courseSlug,
    showCdsDesign,
  } = props;

  const priceExpirationDateString =
    cart && formatDateTimeDisplay(cart.cart.createdAt + MAX_CART_VALID_MILLI_SECS, MED_DATE_ONLY_DISPLAY);
  const reApplyLink = (
    <Link
      standalone={true}
      href={`/learn/${courseSlug}?openFinAid=true`}
      css={css({ padding: '0px !important' })}
      component={TrackedA}
      trackingName="finaid_reapply_from_cart"
    >
      {_t('financial aid')}
    </Link>
  );

  const enrollLink = (
    <Link
      standalone={true}
      href={`/learn/${courseSlug}?action=enroll`}
      css={css({ padding: '0px !important' })}
      component={TrackedA}
      trackingName="finaid_reenroll_from_cart"
    >
      {_t('enroll at the regular price')}
    </Link>
  );
  const finAidErrorMessage = (
    <div>
      <FormattedMessage
        message={_t(
          `The discount provided with your previous application expired on {priceExpirationDateString}. You can re-apply for {financialAidLink} or {enrollLink}.`
        )}
        priceExpirationDateString={priceExpirationDateString}
        financialAidLink={reApplyLink}
        enrollLink={enrollLink}
      />
    </div>
  );
  // Try to get a message corresponding to the payment processor else get message using human readable code.
  const processorResponseCode = submissionErrorServerDetails && submissionErrorServerDetails.processorResponseCode;
  const omitDefaultMessage = [paymentMethods.apple_pay, paymentMethods.google_pay, paymentMethods.klarna].includes(
    submissionErrorCodeMessageProps?.selectedPaymentMethod
  );
  const errorMessage: string | JSX.Element | typeof undefined =
    cart && submissionErrorCode === 'expired-cart' && isFinancialAidCeilingVariantCart(cart, isFinancialAid)
      ? finAidErrorMessage
      : (submissionErrorCode === errorMapping.stripeError && submissionErrorServerDetails?.processorResponseText) ||
        (processorResponseCode && getErrorMessageForPaymentProcessorCode(processorResponseCode)) ||
        getErrorMessageForHumanReadableCode(submissionErrorCode, submissionErrorCodeMessageProps, omitDefaultMessage);

  // if it is invalid coupon or invalid cart, this cart is no longer executable, so delete the local cart.
  if (
    ['invalid-coupon', 'promotion-redemption-limit-reached', 'expired-cart', 'stripe-invalid-currency'].includes(
      submissionErrorCode
    )
  ) {
    savedCartUtils.reset();
  }

  if (!errorMessage) {
    return null;
  }

  const ICON_SIZE = 24;

  return (
    <div role="alert" className="rc-PaymentErrors bgcolor-danger-g1 card-no-action" tabIndex={-1}>
      <TrackedDiv
        className="horizontal-box align-items-top"
        trackingName="payment_error_message"
        data={{ submissionErrorCode, submissionErrorServerDetails }}
        withVisibilityTracking={true}
        style={{ gap: '8px' }}
      >
        {/* minWidth prevents icon from shrinking in mobile layout flexbox */}
        {showCdsDesign ? (
          <ErrorIcon size="medium" color="error" />
        ) : (
          <SvgCircleWarning size={ICON_SIZE} color="#E85628" style={{ minWidth: `${ICON_SIZE}px` }} />
        )}
        <div className="body-1-text">{errorMessage}</div>
      </TrackedDiv>
    </div>
  );
};

export default compose<Props, PaymentErrorsProps>(
  connectToRouter((router) => {
    return {
      isFinancialAid: router?.location?.query?.isFinancialAid || '',
    };
  }),
  withProps<WithCourseIdProps, PaymentErrorsProps>(({ cart }) => ({
    courseId: (cart?.isVC && cart.productItemId) || '',
  })),
  branch<Props>(
    ({ cart, isFinancialAid }) => Boolean(cart && isFinancialAidCeilingVariantCart(cart, isFinancialAid)),
    withCourse()
  )
)(PaymentErrors);
